import { ZodError, z } from 'zod';

import { catchAndLog } from './issue-logging';
import { metadataSchema } from './metadata';

const invalid = z.object({ type: z.literal('INVALID') });

type ValidatedElement<T> = Exclude<T, z.infer<typeof invalid>>;

function skipUnknownVariant(error: ZodError): boolean {
	return error.issues.every(({ code }) => code === 'invalid_union_discriminator');
}

function removeInvalidElements<T extends { type: string }>(elements: T[]): ValidatedElement<T>[] {
	return elements.filter((element): element is ValidatedElement<T> => element.type !== 'INVALID');
}

const exclusion = z.discriminatedUnion('type', [
	z.object({ type: z.literal('form'), form: z.string() }),
	z.object({ type: z.literal('journey'), journey: z.string() }),
	z.object({ type: z.literal('unknown') })
]);

const node = z.object({
	nodeId: z.string(),
	excludedFrom: z
		.array(exclusion)
		.optional()
		.catch(catchAndLog('model-structure', undefined, skipUnknownVariant))
});

const virtualCategory = z.object({
	category: z.literal('virtual'),
	virtualCategory: z.enum(['private', 'commercial']).optional()
});

const nonVirtualCategory = z.object({
	category: z.enum(['private', 'commercial'])
});

const virtualTrim = node.extend(virtualCategory.shape).extend({
	type: z.literal('trim')
});

const nonVirtualTrim = node.extend(nonVirtualCategory.shape).extend({
	type: z.literal('trim'),
	key: z.object({
		carlineId: z.string(),
		salesgroupId: z.string(),
		trimId: z.string()
	})
});

const trim = z.union([virtualTrim, nonVirtualTrim]);

const salesgroupChildren = z
	.array(z.union([trim, invalid]).catch(catchAndLog('model-structure', { type: 'INVALID' } as const)))
	.transform(removeInvalidElements);

const virtualSalesgroup = node.extend(virtualCategory.shape).extend({
	type: z.literal('salesgroup'),
	children: salesgroupChildren
});

const nonVirtualSalesgroup = node.extend(nonVirtualCategory.shape).extend({
	type: z.literal('salesgroup'),
	children: salesgroupChildren,
	key: z.object({
		carlineId: z.string(),
		salesgroupId: z.string()
	})
});

const salesgroup = z.union([virtualSalesgroup, nonVirtualSalesgroup]);

const carlineChildren = z
	.array(z.union([salesgroup, trim, invalid]).catch(catchAndLog('model-structure', { type: 'INVALID' } as const)))
	.transform(removeInvalidElements);

const virtualCarline = node.extend(virtualCategory.shape).extend({
	type: z.literal('carline'),
	children: carlineChildren
});

const nonVirtualCarline = node.extend(nonVirtualCategory.shape).extend({
	type: z.literal('carline'),
	children: carlineChildren,
	key: z.object({
		carlineId: z.string()
	})
});

const carline = z.union([virtualCarline, nonVirtualCarline]);

export const zodModelStructure = z.object({
	meta: metadataSchema.optional(),
	modelElements: z
		.array(
			z.union([carline, salesgroup, trim, invalid]).catch(catchAndLog('model-structure', { type: 'INVALID' } as const))
		)
		.transform(removeInvalidElements)
});

export type ZodModelStructure = z.infer<typeof zodModelStructure>;
export type ZodModelElement = ZodModelStructure['modelElements'][number];
